/**
 * The rules behind the trains card and the sheet's train line — cities plan
 * C4.2. Dependency-free on purpose (two imports of files that are themselves
 * dependency-free), so the Worker's suite can load it: `test/train-plan.test.ts`
 * drives everything here against the captured MBTA fixtures. The hook that
 * fetches is `transit.ts`, which re-exports `trainPlan` under the name the
 * brief gives it.
 *
 * ## What a Train is
 *
 * `departs` and `arrives` are the two ends of the ride as the rider sees it:
 * inbound, Concord → North Station; outbound, North Station → Concord. The
 * MBTA only gives the time *at the stop*, so the far end is that time plus a
 * measured run time (`src/transit/mbta.ts`, `STOPS`) — an estimate, and the
 * card says so where it draws one. `atStop` is the end that is a fact.
 *
 * ## A service day runs to 03:00
 *
 * The 00:29 train belongs to Friday. `serviceDay` is the date three hours
 * ago, which is also what makes 01:00 on Saturday "no more trains tonight"
 * rather than Saturday's last train looking like it is still to come.
 */
import { clockIn, dayIn } from './zone.ts'
import { distanceKm } from './reach.ts'

export type TrainDirection = 'in' | 'out'

/** Same shape as the Worker's `Train` (`src/transit/mbta.ts`); the two files
 *  cannot share it and `test/mbta.test.ts` reads both as text. */
export interface Train { departs: string; arrives: string; direction: TrainDirection; live: boolean; delayMin: number | null }

/** `GET /transit`'s body. */
export interface TransitResponse {
  stop: string
  route: string
  service_date: string
  as_of: string
  source: 'live' | 'schedule'
  trains: Train[]
}

/** The stops the Worker will answer for, and what to call them. The run times
 *  are the Worker's business and live only there. */
export const TRAIN_STOPS: ReadonlyMap<string, { name: string; line: string; route: string }> = new Map([
  ['place-FR-0201', { name: 'Concord', line: 'Fitchburg Line', route: 'CR-Fitchburg' }],
  ['place-FR-0219', { name: 'West Concord', line: 'Fitchburg Line', route: 'CR-Fitchburg' }],
])

/** The MBTA's zone. A train is Boston's wherever the phone is. */
export const TRAIN_ZONE = 'America/New_York'

const MIN = 60_000
const SERVICE_DAY_ENDS_H = 3

/** North Station (`place-north`), the other end of the line. */
export const NORTH_STATION = { lat: 42.3656, lng: -71.0613 }

/** Doug's rule, from the research: the train that gets him to the venue 45
 *  minutes before curtain. */
export const ARRIVE_BEFORE_MIN = 45
/** Straight line to street, and a walking pace: the trip line's own detour
 *  factor (`reach.ts`) at 5 km/h. */
const DETOUR = 1.3
const WALK_KMH = 5
/** Past this many minutes' walk from North Station the leg is the T, and the
 *  T from North Station reaches the whole of Boston and Cambridge in about
 *  this long (Orange to Mass Ave, Red to Harvard). Symphony Hall is a 50
 *  minute walk by the formula and the research called it "about 15 min walk
 *  or two T stops"; neither is the plan, so the plan takes the shorter of the
 *  walk and this. */
export const TRANSIT_LEG_MIN = 25
/** A venue further than this from North Station is not reached by this line. */
export const NORTH_STATION_REACH_KM = 8
/** Predictions beyond this many minutes are not trusted (the brief): the plan
 *  reads the schedule for anything further out. */
export const PREDICTION_HORIZON_MIN = 120
/** A gap between consecutive trains this long is worth saying. The weekend's
 *  is three hours (nothing from Boston 20:40–23:40). */
export const GAP_MIN = 180
/** The card's three each way. */
export const AHEAD = 3
/** A response older than this is not live, whatever its `source` says. */
export const LIVE_FRESH_MIN = 5

const ms = (iso: string): number => Date.parse(iso)

/** The time that is a fact: when an inbound train leaves the stop and when an
 *  outbound one gets there. */
export const atStop = (t: Train): string => (t.direction === 'in' ? t.departs : t.arrives)

/** The service day an instant belongs to, as YYYY-MM-DD in Boston. */
export function serviceDay(at: string | number | Date): string {
  const t = typeof at === 'number' ? at : at instanceof Date ? at.getTime() : ms(at)
  return dayIn(new Date(t - SERVICE_DAY_ENDS_H * 60 * MIN), TRAIN_ZONE)
}

/** `17:25`, in Boston. */
export const trainClock = (iso: string): string => clockIn(iso, TRAIN_ZONE)

// ---------------------------------------------------------------------------
// The plan for an evening in Boston
// ---------------------------------------------------------------------------

/** Minutes on foot from North Station, estimated: straight line × 1.3 at
 *  5 km/h. Labelled "est." wherever it is drawn. */
export function walkMinutes(venue: { lat: number; lng: number }): number {
  const km = distanceKm(venue.lat, venue.lng, NORTH_STATION)!
  return Math.round((km * DETOUR / WALK_KMH) * 60)
}

/** Does this line serve the venue — is it in Boston or Cambridge, a walk or a
 *  T ride from North Station? Lexington is 17 km out and has no line to it. */
export function reachesFromNorthStation(venue: { lat: number; lng: number }): boolean {
  return distanceKm(venue.lat, venue.lng, NORTH_STATION)! <= NORTH_STATION_REACH_KM
}

/** The train as the schedule has it: a prediction undone. Only the end that
 *  was moved by the prediction moves back — both ends inbound (the far end
 *  follows the late departure), the arrival outbound. */
function scheduled(t: Train): Train {
  if (!t.live) return t
  const d = (t.delayMin ?? 0) * MIN
  return t.direction === 'in'
    ? { ...t, departs: new Date(ms(t.departs) - d).toISOString(), arrives: new Date(ms(t.arrives) - d).toISOString(), live: false, delayMin: null }
    : { ...t, arrives: new Date(ms(t.arrives) - d).toISOString(), live: false, delayMin: null }
}

/**
 * The train that gets him there and the last one home.
 *
 * - **there**: the latest inbound train still to leave (`departs >= now`) whose
 *   `arrives` plus the leg from North Station lands 45 minutes or more before
 *   `eventStartIso`. The leg is the walk, or `TRANSIT_LEG_MIN` where the walk
 *   is longer than that. Null when none does — the event is too soon, or
 *   before the first train.
 * - **back**: the last outbound train of the event's service day, provided it
 *   leaves North Station after the event starts and is still to leave. Not
 *   "the last before the event ends" (an end time is rarely known): the last
 *   train is the one a rider plans around. Null on a day with none.
 * - **walkMin**: the straight walk, however long, for the line to print.
 *
 * Schedule or prediction: a train whose time at the stop is more than two
 * hours from `now` is read as scheduled, and the one returned says so.
 */
export function trainPlan(
  eventStartIso: string, venue: { lat: number; lng: number }, trains: readonly Train[], now: string,
): { there: Train | null; back: Train | null; walkMin: number } {
  const walkMin = walkMinutes(venue)
  const leg = Math.min(walkMin, TRANSIT_LEG_MIN)
  const start = ms(eventStartIso)
  const nowMs = ms(now)
  const day = serviceDay(start)

  let there: Train | null = null
  let back: Train | null = null
  for (const raw of trains) {
    // The horizon is measured to the *scheduled* time at the stop: a train 1h45
    // away on the timetable that is 25 minutes late is not "two hours out".
    const schedAtStop = ms(atStop(raw)) - (raw.live ? (raw.delayMin ?? 0) * MIN : 0)
    const t = Math.abs(schedAtStop - nowMs) > PREDICTION_HORIZON_MIN * MIN ? scheduled(raw) : raw
    if (t.direction === 'in') {
      if (ms(t.departs) < nowMs) continue
      if (ms(t.arrives) + leg * MIN > start - ARRIVE_BEFORE_MIN * MIN) continue
      if (!there || ms(t.departs) > ms(there.departs)) there = t
    } else {
      if (serviceDay(t.departs) !== day) continue
      if (ms(t.departs) <= start || ms(t.departs) < nowMs) continue
      if (!back || ms(t.departs) > ms(back.departs)) back = t
    }
  }
  return { there, back, walkMin }
}

/**
 * The sheet's line, or null for an event the line does not serve and a plan
 * with neither half.
 *
 * `17:25 from Concord · last train back 23:50 from North Station`, and a
 * second sentence for the part that is an estimate: when the 17:25 reaches
 * North Station (the run time) and how the last leg is covered. A prediction
 * more than a minute late is said in words.
 */
export function planLines(
  plan: { there: Train | null; back: Train | null; walkMin: number }, from: string,
  /** The service day's long wait out of North Station (`serviceGap`), when it
   *  falls after the event starts: a matinee ends before it, and the line says
   *  where the evening's trains are not. */
  gap: { from: string; to: string } | null = null,
): { text: string; note: string | null } | null {
  const { there, back, walkMin } = plan
  const parts: string[] = []
  if (there) {
    const late = there.live && (there.delayMin ?? 0) >= 2 ? ` (${there.delayMin} min late)` : ''
    parts.push(`${trainClock(there.departs)} from ${from}${late}`)
  }
  if (back) parts.push(`${there ? 'last' : 'Last'} train back ${trainClock(back.departs)} from North Station`)
  if (parts.length === 0) return null
  const leg = walkMin <= TRANSIT_LEG_MIN ? `about ${walkMin} min on foot` : `about ${TRANSIT_LEG_MIN} min on the T`
  const notes = [
    there ? `In at North Station ${trainClock(there.arrives)}, then ${leg}, est.` : null,
    gap ? `No trains from Boston ${trainClock(gap.from)}–${trainClock(gap.to)}.` : null,
  ].filter(Boolean)
  return { text: parts.join(' · '), note: notes.length ? notes.join(' ') : null }
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

/** Is this response good to call live — read from predictions, and recently? */
export function isLive(r: Pick<TransitResponse, 'source' | 'as_of'>, now: Date): boolean {
  return r.source === 'live' && now.getTime() - ms(r.as_of) <= LIVE_FRESH_MIN * MIN
}

/** The next `AHEAD` each way from `now`: inbound by when they leave the stop,
 *  outbound by when they get there. */
export function nextTrains(trains: readonly Train[], now: Date): { toBoston: Train[]; fromBoston: Train[] } {
  const t = now.getTime()
  const upcoming = (dir: TrainDirection) => trains
    .filter(x => x.direction === dir && ms(atStop(x)) >= t)
    .sort((a, b) => ms(atStop(a)) - ms(atStop(b)))
    .slice(0, AHEAD)
  return { toBoston: upcoming('in'), fromBoston: upcoming('out') }
}

/** The last train of a service day each way, by the time at the stop. */
export function lastTrains(trains: readonly Train[], day: string): { in: Train | null; out: Train | null } {
  const last = (dir: TrainDirection): Train | null => {
    let best: Train | null = null
    for (const x of trains) {
      if (x.direction !== dir || serviceDay(atStop(x)) !== day) continue
      if (!best || ms(atStop(x)) > ms(atStop(best))) best = x
    }
    return best
  }
  return { in: last('in'), out: last('out') }
}

/**
 * The long wait inside one service day, if there is one: the two consecutive
 * trains `GAP_MIN` or more apart. Compared by when the train leaves the
 * *start* of its ride (the stop inbound, North Station outbound), because that
 * is the time a person standing there is waiting on. The weekend's outbound
 * gap is 20:40 → 23:40. The night between two service days is not a gap.
 */
export function serviceGap(
  trains: readonly Train[], direction: TrainDirection, day: string,
): { from: string; to: string } | null {
  const times = trains
    .filter(x => x.direction === direction && serviceDay(x.departs) === day)
    .map(x => ms(x.departs))
    .sort((a, b) => a - b)
  for (let i = 1; i < times.length; i++) {
    if (times[i] - times[i - 1] >= GAP_MIN * MIN) {
      return { from: new Date(times[i - 1]).toISOString(), to: new Date(times[i]).toISOString() }
    }
  }
  return null
}

export interface CardView {
  toBoston: Train[]
  fromBoston: Train[]
  /** The footer's lines, already worded. */
  footer: string[]
  /** True when nothing is left tonight in either direction. */
  over: boolean
}

/**
 * Everything the card prints, from the response and the clock. The footer is
 * the last train each way while it is still to come, `No more trains tonight`
 * once both have gone, and the weekend's gap while it is still ahead.
 */
export function cardView(trains: readonly Train[], now: Date): CardView {
  const day = serviceDay(now)
  const { toBoston, fromBoston } = nextTrains(trains, now)
  const last = lastTrains(trains, day)
  const t = now.getTime()
  const goneIn = !last.in || ms(atStop(last.in)) < t
  const goneOut = !last.out || ms(atStop(last.out)) < t
  const footer: string[] = []
  if (goneIn && goneOut) {
    footer.push('No more trains tonight')
  } else {
    const parts: string[] = []
    if (!goneIn) parts.push(`Last to Boston ${trainClock(last.in!.departs)}`)
    if (!goneOut) parts.push(`Last from Boston ${trainClock(last.out!.departs)}`)
    footer.push(parts.join(' · '))
  }
  const gap = serviceGap(trains, 'out', day)
  if (gap && ms(gap.to) > t) footer.push(`No trains from Boston ${trainClock(gap.from)}–${trainClock(gap.to)}`)
  return { toBoston, fromBoston, footer, over: goneIn && goneOut }
}

/** How late a live train is, in the card's words: `+4`, nothing when on time
 *  or unpredicted. A minute is rounding, not lateness. */
export function lateWord(t: Train): string | null {
  return t.live && (t.delayMin ?? 0) >= 2 ? `+${t.delayMin}` : null
}
